import Decimal from 'decimal.js';
import { InvoiceDisplayStatusV2, PaymentDetailType } from 'anchor-scout-common/types';
import { InvoiceDisplayStatus as ExternalInvoiceDisplayStatus } from 'anchor-scout-common/types/external-api/invoice';
import { InvoiceAccountingIntegration } from '../InvoiceAccountingIntegration';
import { InvoiceIntegrationModel, InvoiceIntegrationStatus } from '../InvoiceIntegrationModel';
import { MoneyModel } from '../MoneyModel';
import { formatDate } from '../time/selectors';
const CLIENT_HIDDEN_PAYMENT_DETAILS = new Set([
    PaymentDetailType.PaymentStopped,
    PaymentDetailType.PaymentAwaitingInitiation,
    PaymentDetailType.PaymentScheduled,
    PaymentDetailType.PaymentInitiated,
    PaymentDetailType.PaymentCollected,
    PaymentDetailType.PaidOnDate,
    PaymentDetailType.PaidExternally,
    PaymentDetailType.Voided,
    PaymentDetailType.Disputed
]);
export function filterPaymentDetailsForClient(details) {
    return details.filter((d)=>!CLIENT_HIDDEN_PAYMENT_DETAILS.has(d.type));
}
// Compile-time check: every ExternalInvoiceDisplayStatus value maps to an InvoiceDisplayStatusV2.
// If scout adds a new external value, this Record will fail to typecheck until the FE map is updated.
const EXTERNAL_DISPLAY_STATUS_MAP = {
    [ExternalInvoiceDisplayStatus.Issued]: InvoiceDisplayStatusV2.Issued,
    [ExternalInvoiceDisplayStatus.Pending]: InvoiceDisplayStatusV2.Pending,
    [ExternalInvoiceDisplayStatus.Overdue]: InvoiceDisplayStatusV2.Overdue,
    [ExternalInvoiceDisplayStatus.Processing]: InvoiceDisplayStatusV2.Processing,
    [ExternalInvoiceDisplayStatus.Paid]: InvoiceDisplayStatusV2.Paid,
    [ExternalInvoiceDisplayStatus.RefundFailed]: InvoiceDisplayStatusV2.RefundFailed,
    [ExternalInvoiceDisplayStatus.Refunded]: InvoiceDisplayStatusV2.Refunded,
    [ExternalInvoiceDisplayStatus.PartiallyRefunded]: InvoiceDisplayStatusV2.PartiallyRefunded,
    [ExternalInvoiceDisplayStatus.Voided]: InvoiceDisplayStatusV2.Voided,
    [ExternalInvoiceDisplayStatus.Disputed]: InvoiceDisplayStatusV2.Disputed,
    [ExternalInvoiceDisplayStatus.DisputeWon]: InvoiceDisplayStatusV2.DisputeWon,
    [ExternalInvoiceDisplayStatus.LateReturn]: InvoiceDisplayStatusV2.LateReturn
};
export function fromExternalDisplayStatus(status) {
    return EXTERNAL_DISPLAY_STATUS_MAP[status];
}
export function centsToDecimal(cents) {
    return new Decimal(cents).div(100).toDP(2);
}
export function centsTotals(totals) {
    return {
        subtotal: centsToDecimal(totals.subtotal),
        tax: centsToDecimal(totals.tax),
        withoutCredits: centsToDecimal(totals.withoutCredits),
        credits: centsToDecimal(totals.credits)
    };
}
export function dollarTotals(totals) {
    return {
        subtotal: new Decimal(totals.subtotal),
        tax: new Decimal(totals.tax),
        withoutCredits: new Decimal(totals.withoutCredits),
        credits: new Decimal(totals.credits)
    };
}
export function fromDTO(isVendor, dto) {
    var _dto_paymentMethodDisplay;
    const totalAmountDecimal = new Decimal(dto.totalAmount);
    const issueDate = new Date(dto.issueDate);
    const dueDate = new Date(dto.dueDate);
    const additionalInfo = dto.detailedStatus.additionalInfo;
    return {
        id: dto.id,
        formattedNumber: dto.invoiceNumber,
        issuedOn: formatDate(dto.issueDate),
        dueDate: formatDate(dto.dueDate),
        totalAmount: new MoneyModel(totalAmountDecimal).display(),
        rawTotalAmount: totalAmountDecimal,
        totals: dollarTotals(dto.totals),
        rawIssuedOn: issueDate,
        rawDueDate: dueDate,
        displayStatusV2: fromExternalDisplayStatus(dto.status),
        isTotalAmountZero: totalAmountDecimal.isZero(),
        relationship: {
            id: dto.agreement.id,
            title: dto.agreement.name,
            customer: {
                id: dto.client.business.id,
                name: dto.client.business.name
            },
            vendor: {
                name: dto.serviceProvider.business.name
            }
        },
        integration: reducedIntegrationToModel(dto.integrations.quickbooks),
        xeroIntegration: reducedToInvoiceAccountingIntegration(dto.integrations.xero),
        paymentDetails: isVendor ? additionalInfo : filterPaymentDetailsForClient(additionalInfo),
        pdfExists: dto.pdfAvailable,
        isAdHoc: dto.isAdHoc,
        paymentMethodDisplay: (_dto_paymentMethodDisplay = dto.paymentMethodDisplay) !== null && _dto_paymentMethodDisplay !== void 0 ? _dto_paymentMethodDisplay : null,
        isVoidedFromDispute: dto.isVoidedFromDispute
    };
}
function reducedIntegrationToModel(state) {
    if (state == null) return new InvoiceIntegrationModel({
        status: InvoiceIntegrationStatus.PendingSync
    });
    return InvoiceIntegrationModel.fromExternalDTO(state);
}
function reducedToInvoiceAccountingIntegration(state) {
    const integration = reducedIntegrationToModel(state);
    return new InvoiceAccountingIntegration(integration, integration.isSynced());
}
